import React, { useCallback, useContext, useState } from "react";

// API
import { SendCode } from "../../../Api/TvAuth";

// Context / Hooks
import { UserAuthenticationContext } from "../../../Context/UserAuthenticationContext";
import { useConfirmDeviceCode } from "../../../Hooks/Auth/useConfirmDeviceCode";

// Components
import InputField from "../../../Components/Inputs/InputField";
import ButtonPill from "../../../Components/Buttons/ButtonPill";

// Constants
import { RoutePaths } from "../../../Constants/RoutePaths";

// Assets
import { ReactComponent as IckonicLogoQuestionEverything } from "../../../Assets/Images/Logos/ickonic-question-everything-white.svg";
import IconCheckMarkHollow from "../../../Assets/SVGs/Icons/YesOrNo/CheckMarkHollow";

/**
 * Firestick-specific device linking page.
 *
 * Amazon's Appstore in-app-purchase guidelines forbid directing users to
 * external account creation or purchase flows. So, unlike the shared
 * TvAuthenticationScreen, this page:
 *   - renders WITHOUT the global header/footer (see URLsWithoutUI in RoutePaths,
 *     which is what hides the nav's Sign Up / Subscribe links),
 *   - offers Sign In only — never Sign Up or purchase links.
 *
 * It links the TV via the device-confirm flow (v1/device/confirm through
 * useConfirmDeviceCode) — the same backend as the /Activate page.
 */
const FirestickDeviceCodeAuth = () => {
    const {
        authInitializing,
        doesAuthTokenExist,
        getValidAccessToken,
        userData,
    } = useContext(UserAuthenticationContext);

    const confirmLegacyCode = useCallback(
        async (rawCode: string, controller: AbortController) => {
            if (!userData?.id) return false;
            return SendCode(rawCode, userData.id, controller);
        },
        [userData?.id]
    );

    const { confirmDeviceCode, isConfirming, lastErrorMessage, clearConfirmError } =
        useConfirmDeviceCode({
            getValidAccessToken,
            // /Firestick used the legacy /rdv flow before the modern device-code
            // page launched, so older Fire TV builds still need compatibility.
            legacyNotFoundFallback: confirmLegacyCode,
        });

    const [code, setCode] = useState("");
    const [success, setSuccess] = useState(false);

    // Device confirmation is authorized by the bearer token. Entitlement is
    // enforced when content is requested, not by this linking page. A separate
    // status allow-list here blocked valid lifetime/paused/transitioning accounts
    // even though /Activate could link the same device.
    const isAuthenticated = doesAuthTokenExist();

    // Sign in only — no Sign Up link, per Amazon IAP review. Route through the
    // TV-flow login path (/login/Firestick) so LoginScreen renders its sanitized,
    // purchase-link-free variant (no "Sign Up", no marketing-home logo link) and
    // returns the user here once login succeeds.
    const loginLink = RoutePaths.Login + RoutePaths.Firestick;

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isAuthenticated || isConfirming) return;

        clearConfirmError();
        setSuccess(false);

        const res = await confirmDeviceCode(code);
        if (res.ok) setSuccess(true);
    };

    const codeForm = (
        <>
            <p style={{ marginTop: 0 }}>
                Enter the code shown on your Firestick to link this device to your account.
            </p>

            <form onSubmit={onSubmit}>
                <InputField
                    type="text"
                    name="Device Code"
                    value={code}
                    disabled={isConfirming}
                    label="Device Code"
                    onChange={(e) => {
                        setCode(e.target.value);
                        if (lastErrorMessage) clearConfirmError();
                        if (success) setSuccess(false);
                    }}
                />

                <div className="tv-apps-auth__button-wrapper">
                    <ButtonPill
                        type="submit"
                        disabled={isConfirming || !code.trim()}
                        label={isConfirming ? "Linking…" : "Link device"}
                        className={`button-wrapper__submit ${code.trim() ? "has-value" : "has-no-value"}`}
                    />
                </div>
            </form>

            {lastErrorMessage && (
                <div className="tv-apps-auth__error" style={{ marginTop: 12 }}>
                    ❌ {lastErrorMessage}
                </div>
            )}
        </>
    );

    // Logged out: Sign In only — no Sign Up button.
    const unauthenticatedLayout = (
        <>
            <h3>Sign in to link your Firestick.</h3>

            <div className="tv-apps-auth__button-wrapper">
                <ButtonPill label="Sign In" link={loginLink} />
            </div>
        </>
    );

    const successLayout = (
        <div className="tv-apps-auth__success">
            {IconCheckMarkHollow()}
            Success! Your Firestick will log in shortly. You can go back to your TV now.
        </div>
    );

    const DisplayLayout = () => {
        // Avoid flashing the "Sign in" screen at a logged-in user while the auth
        // bootstrap is still resolving their session.
        if (authInitializing) return <p style={{ marginTop: 0 }}>Checking your account…</p>;
        if (success) return successLayout;
        if (!isAuthenticated) return unauthenticatedLayout;
        return codeForm;
    };

    return (
        <div className="page page--tv-app-auth">
            <div className="tv-apps-auth__logo">
                <IckonicLogoQuestionEverything />
            </div>

            <div className="tv-apps-auth__inner">
                <h2>Firestick Activation</h2>

                <div className="tv-apps-auth__divider" />

                <div className="tv-apps-auth__content">
                    {DisplayLayout()}
                </div>
            </div>
        </div>
    );
};

export default FirestickDeviceCodeAuth;
